import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { 
  Plus, Search, Filter, Eye, AlertTriangle, Clock, 
  CheckCircle2, XCircle, FileText, Users, RefreshCw,
  Calendar, User, Inbox, List, Ban, Undo2
} from "lucide-react";
import Layout from "../components/Layout";
import { TerminarContratoDialog } from "../components/propiedad/AccionesRapidas";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const BUCKETS = [
  { key: "vencido", label: "Vencido", headerClass: "bg-red-50 text-red-700 border-red-200", badgeClass: "bg-red-100 text-red-700" },
  { key: "treinta", label: "Vence en menos de 30 días", headerClass: "bg-orange-50 text-orange-700 border-orange-200", badgeClass: "bg-orange-100 text-orange-700" },
  { key: "sesenta", label: "Vence en menos de 60 días", headerClass: "bg-amber-50 text-amber-700 border-amber-200", badgeClass: "bg-amber-100 text-amber-700" },
  { key: "noventa", label: "Vence en menos de 90 días", headerClass: "bg-yellow-50 text-yellow-700 border-yellow-200", badgeClass: "bg-yellow-100 text-yellow-700" },
  { key: "renovacion_iniciada", label: "Renovación iniciada", headerClass: "bg-indigo-50 text-indigo-700 border-indigo-200", badgeClass: "bg-indigo-100 text-indigo-700" },
  { key: "renovacion_firmada", label: "Renovación firmada", headerClass: "bg-emerald-50 text-emerald-700 border-emerald-200", badgeClass: "bg-emerald-100 text-emerald-700" },
  { key: "no_se_renovara", label: "No se renovará", headerClass: "bg-gray-50 text-gray-600 border-gray-200", badgeClass: "bg-gray-200 text-gray-600" },
];

const ESTATUS_OPTIONS = [
  { value: "Por preparar", label: "Por preparar", color: "bg-gray-100 text-gray-800" },
  { value: "Enviado a dueño", label: "Enviado a dueño", color: "bg-blue-100 text-blue-800" },
  { value: "Firmado por dueño", label: "Firmado por dueño", color: "bg-indigo-100 text-indigo-800" },
  { value: "Enviado a inquilino/fiador", label: "Enviado a inquilino/fiador", color: "bg-purple-100 text-purple-800" },
  { value: "Recibido firmado", label: "Recibido firmado", color: "bg-emerald-100 text-emerald-800" },
  { value: "Validado y archivado", label: "Validado y archivado", color: "bg-green-100 text-green-800" },
  { value: "Cancelado", label: "Cancelado", color: "bg-red-100 text-red-800" },
];

const EN_MANOS_OPTIONS = [
  { value: "Oficina", label: "Oficina" },
  { value: "Dueño", label: "Dueño" },
  { value: "Inquilino", label: "Inquilino" },
  { value: "Fiador", label: "Fiador" },
];

const getEstatusBadge = (estatus) => {
  const option = ESTATUS_OPTIONS.find(o => o.value === estatus);
  return option ? option.color : "bg-gray-100 text-gray-800";
};

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  try {
    return new Date(dateStr).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

export default function Renovaciones({ user, onLogout }) {
  const navigate = useNavigate();
  const [renovaciones, setRenovaciones] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [estatusFilter, setEstatusFilter] = useState("todos");
  const [enManosFilter, setEnManosFilter] = useState("todos");
  const [showVencidas, setShowVencidas] = useState(false);
  const [showAtoradas, setShowAtoradas] = useState(false);
  const [vista, setVista] = useState("bandeja");
  const [bandeja, setBandeja] = useState(null);
  const [terminarInfo, setTerminarInfo] = useState(null);
  
  // Modal for new renovation
  const [showNewModal, setShowNewModal] = useState(false);
  const [contratos, setContratos] = useState([]);
  const [newRenovacion, setNewRenovacion] = useState({
    contrato_id: "",
    tiene_fiador: false,
    fecha_limite: "",
    responsable_interno: "",
    observaciones: ""
  });

  useEffect(() => {
    fetchData();
  }, [estatusFilter, enManosFilter, showVencidas, showAtoradas]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      
      let url = `${API}/renovaciones?`;
      if (estatusFilter !== "todos") url += `estatus=${estatusFilter}&`;
      if (enManosFilter !== "todos") url += `en_manos_de=${enManosFilter}&`;
      if (showVencidas) url += `vencidas=true&`;
      if (showAtoradas) url += `atoradas_dias=7&`;
      if (searchTerm) url += `search=${searchTerm}&`;
      
      const [renovacionesRes, statsRes, bandejaRes] = await Promise.all([
        axios.get(url, { headers }),
        axios.get(`${API}/renovaciones/stats/summary`, { headers }),
        axios.get(`${API}/renovaciones-bandeja`, { headers })
      ]);
      
      setRenovaciones(renovacionesRes.data);
      setStats(statsRes.data);
      setBandeja(bandejaRes.data);
    } catch (error) {
      console.error(error);
      toast.error("Error al cargar renovaciones");
    } finally {
      setLoading(false);
    }
  };

  const fetchContratos = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/contracts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter contracts that can have renovations (exclude archived and cancelled)
      const disponibles = response.data.filter(c => 
        c.estatus === "Activo" || 
        c.estatus === "Próximo a vencer" || 
        c.estatus === "Por comenzar" ||
        c.estatus === "Vencido"
      );
      setContratos(disponibles);
    } catch (error) {
      toast.error("Error al cargar contratos");
    }
  };

  const handleOpenNewModal = (contratoId = "") => {
    fetchContratos();
    setNewRenovacion({
      contrato_id: contratoId || "",
      tiene_fiador: false,
      fecha_limite: "",
      responsable_interno: "",
      observaciones: ""
    });
    setShowNewModal(true);
  };

  const handleNoRenovar = async (contratoId, flag) => {
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/contracts/${contratoId}/no-renovar`, { no_renovar: flag }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(flag ? "Contrato marcado como 'No se renovará'" : "Marca eliminada");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar el contrato");
    }
  };

  const handleCreateRenovacion = async () => {
    if (!newRenovacion.contrato_id) {
      toast.error("Selecciona un contrato");
      return;
    }
    
    try {
      const token = localStorage.getItem("token");
      await axios.post(`${API}/renovaciones`, newRenovacion, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success("Renovación creada exitosamente");
      setShowNewModal(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear renovación");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchData();
  };

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="text-lg">Cargando...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6 fade-in">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Renovaciones</h1>
            <p className="text-gray-600">Bandeja de vencimientos y seguimiento de renovaciones</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={vista === "bandeja" ? "default" : "outline"}
              size="sm"
              onClick={() => setVista("bandeja")}
              data-testid="vista-bandeja-btn"
            >
              <Inbox className="w-4 h-4 mr-1" /> Bandeja
            </Button>
            <Button
              variant={vista === "bitacora" ? "default" : "outline"}
              size="sm"
              onClick={() => setVista("bitacora")}
              data-testid="vista-bitacora-btn"
            >
              <List className="w-4 h-4 mr-1" /> Bitácora
            </Button>
            <Button onClick={() => handleOpenNewModal()} data-testid="nueva-renovacion-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Renovación
            </Button>
          </div>
        </div>

        {/* Bandeja de vencimientos */}
        {vista === "bandeja" && bandeja && (
          <div className="space-y-4" data-testid="bandeja-renovaciones">
            {BUCKETS.every((b) => (bandeja[b.key] || []).length === 0) && (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-emerald-300" />
                  No hay contratos próximos a vencer ni renovaciones pendientes
                </CardContent>
              </Card>
            )}
            {BUCKETS.map((bucket) => {
              const items = bandeja[bucket.key] || [];
              if (items.length === 0) return null;
              return (
                <Card key={bucket.key} data-testid={`bucket-${bucket.key}`}>
                  <div className={`px-4 py-2.5 border-b rounded-t-xl flex items-center justify-between ${bucket.headerClass}`}>
                    <span className="text-sm font-semibold">{bucket.label}</span>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${bucket.badgeClass}`}>{items.length}</span>
                  </div>
                  <CardContent className="p-0 overflow-x-auto">
                    <table className="min-w-full text-sm">
                      <tbody className="divide-y divide-gray-100">
                        {items.map((it) => (
                          <tr key={it.contrato_id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <button
                                onClick={() => it.property_id && navigate(`/propiedades/${it.property_id}`)}
                                className="text-indigo-600 hover:underline font-medium text-left"
                                data-testid={`bandeja-propiedad-${it.contrato_id}`}
                              >
                                {it.inmueble_nombre}
                              </button>
                              <p className="text-xs text-gray-500">{it.numero_contrato} · Inq: {it.inquilino_nombre}</p>
                            </td>
                            <td className="px-4 py-3 text-gray-700 hidden md:table-cell">{it.propietario_nombre}</td>
                            <td className="px-4 py-3 whitespace-nowrap">
                              <p className="text-gray-900">{formatDate(it.fecha_termino)}</p>
                              <p className={`text-xs font-medium ${it.dias_para_vencimiento < 0 ? "text-red-600" : it.dias_para_vencimiento <= 30 ? "text-orange-600" : "text-gray-500"}`}>
                                {it.dias_para_vencimiento < 0
                                  ? `Vencido hace ${Math.abs(it.dias_para_vencimiento)} días`
                                  : `${it.dias_para_vencimiento} días restantes`}
                              </p>
                            </td>
                            <td className="px-4 py-3 hidden lg:table-cell">
                              {it.renovacion ? (
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getEstatusBadge(it.renovacion.estatus)}`}>
                                  {it.renovacion.folio} · {it.renovacion.estatus}
                                </span>
                              ) : it.no_renovar ? (
                                <span className="text-xs text-gray-500 italic">{it.motivo_no_renovar || "Sin renovación"}</span>
                              ) : (
                                <span className="text-xs text-gray-400">Sin renovación iniciada</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-right whitespace-nowrap">
                              <div className="flex gap-1.5 justify-end">
                                {it.renovacion ? (
                                  <Button size="sm" variant="outline" onClick={() => navigate(`/renovaciones/${it.renovacion.id}`)} data-testid={`ver-renovacion-${it.contrato_id}`}>
                                    <Eye className="w-3.5 h-3.5 mr-1" /> Ver
                                  </Button>
                                ) : !it.no_renovar ? (
                                  <Button size="sm" onClick={() => handleOpenNewModal(it.contrato_id)} data-testid={`iniciar-renovacion-${it.contrato_id}`}>
                                    <Plus className="w-3.5 h-3.5 mr-1" /> Iniciar
                                  </Button>
                                ) : null}
                                {it.no_renovar ? (
                                  <>
                                    <Button size="sm" variant="outline" onClick={() => handleNoRenovar(it.contrato_id, false)} title="Quitar marca" data-testid={`quitar-no-renovar-${it.contrato_id}`}>
                                      <Undo2 className="w-3.5 h-3.5" />
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-red-600 border-red-200 hover:bg-red-50"
                                      onClick={() => setTerminarInfo({ id: it.contrato_id, numero: it.numero_contrato })}
                                      data-testid={`terminar-desde-bandeja-${it.contrato_id}`}
                                    >
                                      Terminar
                                    </Button>
                                  </>
                                ) : !it.renovacion ? (
                                  <Button size="sm" variant="outline" onClick={() => handleNoRenovar(it.contrato_id, true)} title="No se renovará" data-testid={`marcar-no-renovar-${it.contrato_id}`}>
                                    <Ban className="w-3.5 h-3.5" />
                                  </Button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {vista === "bitacora" && (<>
        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Total</p>
                    <p className="text-2xl font-bold">{stats.total}</p>
                  </div>
                  <FileText className="w-8 h-8 text-gray-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">En Oficina</p>
                    <p className="text-2xl font-bold">{stats.por_en_manos_de?.["Oficina"] || 0}</p>
                  </div>
                  <Users className="w-8 h-8 text-blue-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">Pend. Firma</p>
                    <p className="text-2xl font-bold">{stats.pendientes_firma}</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-orange-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-red-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-red-500">Vencidas</p>
                    <p className="text-2xl font-bold text-red-600">{stats.vencidas}</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-red-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-amber-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-amber-500">Atoradas +7d</p>
                    <p className="text-2xl font-bold text-amber-600">{stats.atoradas_7_dias}</p>
                  </div>
                  <Clock className="w-8 h-8 text-amber-400" />
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-white border-green-200">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-green-500">Archivadas</p>
                    <p className="text-2xl font-bold text-green-600">
                      {stats.por_estatus?.["Validado y archivado"] || 0}
                    </p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 text-green-400" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap gap-4 items-end">
              <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
                <Label className="text-xs text-gray-500">Buscar</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="Folio, inmueble, inquilino..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="flex-1"
                  />
                  <Button type="submit" variant="outline" size="icon">
                    <Search className="w-4 h-4" />
                  </Button>
                </div>
              </form>
              
              <div className="w-[180px]">
                <Label className="text-xs text-gray-500">Estatus</Label>
                <Select value={estatusFilter} onValueChange={setEstatusFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {ESTATUS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="w-[150px]">
                <Label className="text-xs text-gray-500">En manos de</Label>
                <Select value={enManosFilter} onValueChange={setEnManosFilter}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos</SelectItem>
                    {EN_MANOS_OPTIONS.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={showVencidas}
                    onCheckedChange={setShowVencidas}
                  />
                  <span className="text-sm text-red-600">Vencidas</span>
                </label>
                
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={showAtoradas}
                    onCheckedChange={setShowAtoradas}
                  />
                  <span className="text-sm text-amber-600">Atoradas +7d</span>
                </label>
              </div>
              
              <Button variant="ghost" size="sm" onClick={() => {
                setSearchTerm("");
                setEstatusFilter("todos");
                setEnManosFilter("todos");
                setShowVencidas(false);
                setShowAtoradas(false);
              }}>
                <RefreshCw className="w-4 h-4 mr-1" />
                Limpiar
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Folio</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inmueble</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inquilino</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estatus</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">En manos de</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Firmas</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fecha límite</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Días</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Responsable</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {renovaciones.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="px-6 py-12 text-center text-gray-500">
                        No se encontraron renovaciones
                      </td>
                    </tr>
                  ) : (
                    renovaciones.map((ren) => (
                      <tr 
                        key={ren.id} 
                        className={`hover:bg-gray-50 ${ren.vencida ? 'bg-red-50' : ''} ${ren.dias_en_estatus > 7 && !['Validado y archivado', 'Cancelado'].includes(ren.estatus) ? 'bg-amber-50' : ''}`}
                      >
                        <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                          {ren.folio}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-[150px] truncate" title={ren.inmueble_nombre}>
                          {ren.inmueble_nombre}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 max-w-[120px] truncate" title={ren.inquilino_nombre}>
                          {ren.inquilino_nombre}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getEstatusBadge(ren.estatus)}`}>
                            {ren.estatus}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900">
                          {ren.en_manos_de}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className={`font-medium ${ren.firmas_faltantes > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                            {ren.firmas_total - ren.firmas_faltantes}/{ren.firmas_total}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          {ren.fecha_limite ? (
                            <span className={ren.vencida ? 'text-red-600 font-medium' : 'text-gray-900'}>
                              {formatDate(ren.fecha_limite)}
                              {ren.vencida && <AlertTriangle className="w-4 h-4 inline ml-1" />}
                            </span>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <span className={`${ren.dias_en_estatus > 7 && !['Validado y archivado', 'Cancelado'].includes(ren.estatus) ? 'text-amber-600 font-medium' : 'text-gray-500'}`}>
                            {ren.dias_en_estatus}d
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 max-w-[100px] truncate">
                          {ren.responsable_interno || "-"}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => navigate(`/renovaciones/${ren.id}`)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        </>)}
      </div>

      {/* Terminar contrato desde bandeja */}
      {terminarInfo && (
        <TerminarContratoDialog
          open={!!terminarInfo}
          onOpenChange={(v) => !v && setTerminarInfo(null)}
          contratoId={terminarInfo.id}
          contratoNumero={terminarInfo.numero}
          onSuccess={fetchData}
        />
      )}

      {/* New Renovation Modal */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nueva Renovación</DialogTitle>
            <DialogDescription>
              Crea un registro de renovación para dar seguimiento a la firma del contrato.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Contrato *</Label>
              <Select 
                value={newRenovacion.contrato_id} 
                onValueChange={(v) => setNewRenovacion({...newRenovacion, contrato_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleccionar contrato..." />
                </SelectTrigger>
                <SelectContent>
                  {contratos.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.numero_contrato} - {c.inmueble_nombre}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Checkbox
                id="tiene_fiador"
                checked={newRenovacion.tiene_fiador}
                onCheckedChange={(checked) => setNewRenovacion({...newRenovacion, tiene_fiador: checked})}
              />
              <Label htmlFor="tiene_fiador">Tiene fiador</Label>
            </div>
            
            <div>
              <Label>Fecha límite para firma</Label>
              <Input
                type="date"
                value={newRenovacion.fecha_limite}
                onChange={(e) => setNewRenovacion({...newRenovacion, fecha_limite: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Responsable interno</Label>
              <Input
                placeholder="Nombre del responsable..."
                value={newRenovacion.responsable_interno}
                onChange={(e) => setNewRenovacion({...newRenovacion, responsable_interno: e.target.value})}
              />
            </div>
            
            <div>
              <Label>Observaciones</Label>
              <Textarea
                placeholder="Notas adicionales..."
                value={newRenovacion.observaciones}
                onChange={(e) => setNewRenovacion({...newRenovacion, observaciones: e.target.value})}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewModal(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreateRenovacion}>
              Crear Renovación
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
